import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, Package, Tag } from "lucide-react";

import OpenWorkPanel from "../../components/OpenWorkPanel";
import Panel from "../../components/Panel";
import StatCard from "../../components/StatCard";
import { EmptyState, PageHeader } from "../../components/erp";
import { businessProductsAPI } from "../../services/businessProductsService";

function centsToPeso(cents) {
  return `₱${(Number(cents || 0) / 100).toLocaleString("en-PH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function classifyStock(stock, threshold = 10) {
  const quantity = Number(stock ?? 0);
  if (quantity <= 0) return "out";
  if (quantity <= threshold) return "low";
  return "in";
}

export default function MerchandisingDashboard() {
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        businessProductsAPI.getProductSalesReport(),
        businessProductsAPI.getCategories(),
      ]);
      const products = Array.isArray(productsRes)
        ? productsRes
        : Array.isArray(productsRes?.data?.items)
          ? productsRes.data.items
          : Array.isArray(productsRes?.data)
            ? productsRes.data
            : Array.isArray(productsRes?.items)
              ? productsRes.items
              : [];
      setRows(products);
      setCategories(Array.isArray(categoriesRes?.data) ? categoriesRes.data : Array.isArray(categoriesRes) ? categoriesRes : []);
    } catch {
      setRows([]);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const summary = useMemo(() => {
    const listed = rows.filter((row) => String(row.status || "").toUpperCase() === "LISTED");
    const draft = rows.filter((row) => String(row.status || "").toUpperCase() === "DRAFT");
    const missingCategory = rows.filter((row) => row.category_id == null);
    const missingMedia = rows.filter((row) => !row.image);
    const lowStock = rows.filter((row) => classifyStock(row.stock, row.low_stock_threshold ?? 10) === "low");
    const lowStockListed = listed.filter((row) => classifyStock(row.stock, row.low_stock_threshold ?? 10) === "low");
    const categoryCounts = new Map();
    rows.forEach((row) => {
      const key = row.category_name || "Uncategorized";
      categoryCounts.set(key, (categoryCounts.get(key) || 0) + 1);
    });
    const categoryBreakdown = [...categoryCounts.entries()]
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
      .slice(0, 8);
    const revenueLeaders = [...rows]
      .sort((a, b) => Number(b.revenue || 0) - Number(a.revenue || 0))
      .slice(0, 8);
    const openWorkItems = [
      draft.length ? {
        id: "merchandising-drafts",
        title: "Draft products",
        description: "Catalog records are still unpublished or incomplete.",
        count: draft.length,
        tone: "amber",
        href: "/business/merchandising/open-work?lane=draft",
        actionLabel: "Open queue",
      } : null,
      missingCategory.length ? {
        id: "merchandising-category",
        title: "Products missing category",
        description: "Some products still have no category assignment.",
        count: missingCategory.length,
        tone: "rose",
        href: "/business/merchandising/open-work?lane=category",
        actionLabel: "Open queue",
      } : null,
      missingMedia.length ? {
        id: "merchandising-media",
        title: "Products missing media",
        description: "These products still have no catalog image.",
        count: missingMedia.length,
        tone: "blue",
        href: "/business/merchandising/open-work?lane=media",
        actionLabel: "Open queue",
      } : null,
      lowStockListed.length ? {
        id: "merchandising-low-stock",
        title: "Listed products with low stock",
        description: "Merchandising needs replenishment coordination or storefront messaging follow-through.",
        count: lowStockListed.length,
        tone: "amber",
        href: "/business/merchandising/open-work?lane=stock",
        actionLabel: "Open queue",
      } : null,
    ].filter(Boolean);
    return {
      listed,
      draft,
      missingCategory,
      missingMedia,
      lowStock,
      categoryBreakdown,
      revenueLeaders,
      openWorkItems,
    };
  }, [rows]);

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Merchandising"
        title="Merchandising Dashboard"
        subtitle="Use merchandising as the command center for catalog readiness, product completeness, and stock-aware storefront follow-through."
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard title="All Products" value={loading ? "—" : rows.length} icon={Package} />
        <StatCard title="Listed" value={loading ? "—" : summary.listed.length} icon={Package} />
        <StatCard title="Drafts" value={loading ? "—" : summary.draft.length} icon={AlertTriangle} accent />
        <StatCard title="Low Stock" value={loading ? "—" : summary.lowStock.length} icon={AlertTriangle} accent />
      </div>

      <OpenWorkPanel
        title="Merchandising Open Work Snapshot"
        subtitle="Catalog records that still need category, media, completion, or stock-aware follow-through."
        items={loading ? [] : summary.openWorkItems}
        emptyTitle="Merchandising queue is clear"
        emptyDescription="No draft, category, media, or stock-readiness issues are currently open."
      />

      <div className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
        <Panel
          title="Category Coverage"
          subtitle="Product distribution across the current merchandising catalog"
          right={
            <div className="text-sm text-neutral-500 dark:text-neutral-400">
              {loading ? "—" : `${categories.length} category records`}
            </div>
          }
        >
          {!loading && !summary.categoryBreakdown.length ? (
            <EmptyState icon={Tag} title="No category coverage yet" description="Category coverage appears here once product records are available." />
          ) : (
            <div className="space-y-3">
              {summary.categoryBreakdown.map((row) => (
                <div key={row.name} className="flex items-center justify-between rounded-xl border border-neutral-200 bg-neutral-50 px-4 py-3 dark:border-neutral-800 dark:bg-neutral-900/40">
                  <div className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{row.name}</div>
                  <div className="rounded-full border border-neutral-200 bg-white px-2 py-0.5 text-xs font-semibold text-neutral-700 dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-200">
                    {row.count}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel
          title="Top Revenue Products"
          subtitle="Products currently leading recent merchandising value"
          right={
            <Link
              to="/business/products"
              className="inline-flex items-center gap-1 text-sm font-medium text-neutral-600 hover:text-neutral-900 dark:text-neutral-300 dark:hover:text-neutral-100"
            >
              All products <ArrowRight className="h-4 w-4" />
            </Link>
          }
        >
          {!loading && !summary.revenueLeaders.length ? (
            <EmptyState icon={Package} title="No products yet" description="Product records will appear here once the merchandising catalog is populated." />
          ) : (
            <div className="space-y-3">
              {summary.revenueLeaders.map((product) => (
                <div key={product.id} className="rounded-xl border border-neutral-200 bg-neutral-50 p-4 dark:border-neutral-800 dark:bg-neutral-900/40">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{product.name || `Product #${product.id}`}</div>
                      <div className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                        {product.category_name || "Uncategorized"} · Sold {Number(product.soldQty || 0).toLocaleString("en-PH")}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{centsToPeso(product.base_price_cents)}</div>
                      <div className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">{centsToPeso(product.revenue)}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
